import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import { Box, Chip, List, ListItemText, Skeleton, Stack, Typography } from '@mui/material';
import { find, isEmpty, map, times } from 'es-toolkit/compat';
import React from 'react';

import { JobStatus, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { formatRelativeTimeForUser } from 'utils/dateTime';
import { humanize } from 'utils/helpers';
import { useJobsQuery } from 'utils/queryHooks/jobs/useJobs';
import { useStudies } from 'utils/queryHooks/useStudies';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useCurrentUser } from 'utils/useCurrentUser';
import { usePermissions } from 'utils/usePermissions';

interface Props {
  selectedStudyId?: string;
}

const compactStatuses = [JobStatus.Pending, JobStatus.Running, JobStatus.FailedToStart] as const;

const statusColorByValue: Record<string, 'default' | 'warning' | 'info' | 'error'> = {
  [JobStatus.Pending]: 'warning',
  [JobStatus.Running]: 'info',
  [JobStatus.FailedToStart]: 'error',
};

export const CopilotBackgroundTasksTab: React.FC<React.PropsWithChildren<Props>> = ({ selectedStudyId }) => {
  const currentUser = useCurrentUser();
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const { data: studies } = useStudies(labId, false, { enabled: Boolean(labId) });
  const canViewJobs = hasPermission(Permission.ViewJobs);

  const jobsQuery = useJobsQuery(
    {
      labId,
      page: 1,
      pageSize: 10,
      filters: {
        studyId: selectedStudyId,
        users: currentUser?.userId ? [{ id: currentUser.userId, email: '' }] : undefined,
        statuses: [...compactStatuses],
      },
    },
    {
      enabled: Boolean(canViewJobs && labId && currentUser?.userId),
      queriedEntityName: 'jobs',
      keepPreviousData: true,
    }
  );

  if (!canViewJobs) {
    return (
      <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          You do not have permission to view jobs.
        </Typography>
      </Box>
    );
  }

  if (jobsQuery.isLoading) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading jobs...
        </Typography>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {times(3, (index) => (
            <Box key={index} sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Skeleton variant="rounded" width={index === 1 ? '62%' : '74%'} height={18} sx={{ mb: 0.75 }} />
                    <Skeleton variant="text" width={index === 2 ? '32%' : '40%'} height={14} />
                  </Box>
                  <Skeleton variant="rounded" width={88} height={24} />
                </Stack>
                <Skeleton variant="text" width={index === 0 ? '28%' : '24%'} height={14} />
                <Skeleton variant="text" width={index === 2 ? '34%' : '42%'} height={14} />
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  if (isEmpty(jobsQuery.data?.jobs)) {
    return (
      <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
        <PendingActionsRoundedIcon color="disabled" sx={{ fontSize: 28, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No active jobs for the current workspace context.
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {map(jobsQuery.data.jobs, (job) => {
        const studyName = find(studies, { id: job.studyId })?.name || job.studyId || 'No study';
        const updatedAtLabel = formatRelativeTimeForUser(job.updatedAt);

        return (
          <Box key={job.id} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {job.name || humanize(job.type || JobType.StudyReport)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {studyName}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={statusColorByValue[job.status || ''] || 'default'}
                  label={humanize(job.status || 'unknown')}
                />
              </Stack>
              <ListItemText
                disableTypography
                primary={
                  <Typography variant="caption" color="text.secondary">
                    {humanize(job.type || 'job')}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {updatedAtLabel ? `Updated ${updatedAtLabel}` : 'Recently queued'}
                  </Typography>
                }
              />
            </Stack>
          </Box>
        );
      })}
    </List>
  );
};
